import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/site/AuthShell";
import { FieldError, fieldErrors, Notice, ProblemAlert } from "@/components/site/Feedback";
import { Turnstile, useTurnstileState } from "@/components/site/Turnstile";
import { api } from "@/lib/api";

export function ForgotPasswordPage() {
  usePageTitle("Forgot password");
  const turnstile = useTurnstileState();
  const [email, setEmail] = useState("");
  const send = useMutation({
    mutationFn: () => api.auth.forgotPassword(email.trim(), turnstile.token),
    onSettled: () => turnstile.reset(),
  });

  return (
    <AuthShell
      eyebrow="Members' portal"
      title="Reset your password"
      subtitle="Enter the email address of your account and we'll send you a link to choose a new password."
    >
      {send.isSuccess ? (
        <Notice tone="success" title="Check your inbox">
          If an account exists for {email}, a password reset link is on its way. The link is valid for a limited time.
        </Notice>
      ) : (
        <form
          className="space-y-5"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!turnstile.missing) send.mutate();
          }}
        >
          <ProblemAlert error={send.error} />
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            <FieldError messages={fieldErrors(send.error, "Email")} />
          </div>
          <Turnstile ref={turnstile.ref} onToken={turnstile.setToken} onEnabled={turnstile.setEnabled} />
          <Button type="submit" variant="gold" size="lg" className="w-full" disabled={send.isPending || !email || turnstile.missing}>
            {send.isPending && <Loader2 className="animate-spin" />}
            Send reset link
          </Button>
        </form>
      )}
      <p className="mt-6 text-sm text-muted-foreground">
        Remembered it?{" "}
        <Link to="/login" className="font-medium text-foreground underline underline-offset-4">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
